import classNames from 'classnames';
import { connect } from 'react-redux';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  Alert,
  Button,
  Card,
  Checkbox,
  HStack,
  Typography,
  VStack,
} from '@vivaahealth/design-system';

import * as api from '../../../api';
import * as store from '../../../reducers/store';
import { getFormattedCurrencyAmount } from '../PaymentsPage/utils';

import { Page } from '..';
import { PatientRouteParams } from '../../router';
import { PaymentContext } from '../PaymentInfoProvider';
import { PriorBalance } from './PriorBalance';
import { isPartialBalanceValid, showPayments } from './utils';

import introPageStyles from '../../FormStart/IntroPage/index.module.css';
import paymentsStyles from '../index.module.css';
import styles from './index.module.css';

interface Props extends StateProps {
  navigate: (page: Page) => void;
  selectedFormSetId: string;
  paymentPrimitiveConfigs: api.PrevisitFormTemplateFieldConfig.PaymentPrimitiveConfigs;
  infoMessage?: string;
}

interface StateProps {
  copayPrepayDisplaySetting: api.PracticePreference.CopayPrepayDisplaySetting;
  previousBalanceCollectionSetting: api.PracticePreference.PreviousBalanceCollectionSetting;
}

export type IsCheckedObj = {
  copayCheck: boolean;
  prepayCheck: boolean;
  balanceCheck: boolean;
  partialBalanceCheck: boolean;
};

export type IsCheckedObjKey = keyof IsCheckedObj;

export type PartialBalanceInfo = {
  isEnabled: boolean;
  amount: number | null;
  error: string;
};

type DisplayFeeTypes = 'co-pay' | 'visit fee' | 'prepayment' | 'prior balance';

const BalancePage: React.FC<Props> = ({
  selectedFormSetId,
  navigate,
  infoMessage,
  copayPrepayDisplaySetting,
  previousBalanceCollectionSetting,
  paymentPrimitiveConfigs,
}) => {
  const { t } = useTranslation();
  const { data: paymentInfo, setData } = useContext(PaymentContext);
  const { authenticationCode } = useParams<PatientRouteParams>();

  const [loading, setLoading] = useState(false);
  const [isCheckedObj, setIsCheckedObj] = useState<IsCheckedObj>({
    copayCheck: false,
    prepayCheck: false,
    balanceCheck: false,
    partialBalanceCheck: false,
  });
  const [partialBalanceInfo, setPartialBalanceInfo] =
    useState<PartialBalanceInfo>({
      isEnabled:
        previousBalanceCollectionSetting ===
        api.PracticePreference.PreviousBalanceCollectionSetting.FullAndPartial,
      amount: null,
      error: '',
    });
  const [displayTypes, setDisplayTypes] = useState<DisplayFeeTypes[]>([]);
  const feeDescription =
    paymentInfo?.feeType === api.Payment.FeeType.CoPay ? 'Co-pay' : 'Visit fee';

  const toggleCheck = useCallback(
    ({ key, force }: { key: IsCheckedObjKey; force?: boolean }) => {
      force !== undefined
        ? setIsCheckedObj((prevState) => ({ ...prevState, [key]: force }))
        : setIsCheckedObj((prevState) => ({
            ...prevState,
            [key]: !prevState[key],
          }));
    },
    [setIsCheckedObj],
  );

  const { showCopay, showPrepay, showPreviousBalance } = useMemo(
    () =>
      showPayments({
        copayPrepayDisplaySetting,
        previousBalanceCollectionSetting,
        visitFeeAmount: paymentInfo?.visitFeeAmount,
        prepayAmount: paymentInfo?.prepayAmount,
        previousBalance: paymentInfo?.previousBalance,
        paymentPrimitiveConfigs,
      }),
    [
      copayPrepayDisplaySetting,
      previousBalanceCollectionSetting,
      paymentInfo,
      paymentPrimitiveConfigs,
    ],
  );

  useEffect(() => {
    if (!paymentInfo) return;
    const displays: DisplayFeeTypes[] = [];
    // hide copay if prepay selection is disabled + a non-zero/non-null prepay is present
    if (showCopay) {
      setIsCheckedObj((prevState) => ({
        ...prevState,
        copayCheck: true,
      }));
      displays.push(feeDescription.toLowerCase() as DisplayFeeTypes);
    }
    if (showPrepay) {
      setIsCheckedObj((prevState) => ({
        ...prevState,
        prepayCheck: true,
      }));
      displays.push('prepayment');
    }
    if (showPreviousBalance) {
      setIsCheckedObj((prevState) => ({
        ...prevState,
        balanceCheck: true,
      }));
      displays.push('prior balance');
    }
    setDisplayTypes(displays);
  }, [
    paymentInfo,
    showCopay,
    showPrepay,
    showPreviousBalance,
    feeDescription,
    setIsCheckedObj,
  ]);

  if (!paymentInfo) return null;

  const handleContinue = () => {
    paymentInfo.paymentMethods.length
      ? navigate(Page.Select)
      : navigate(Page.Add);
  };

  const updatePaymentAmountAndContinue = async () => {
    if (partialBalanceInfo.error) {
      return;
    }

    let paymentType = api.Payment.PaymentType.Total;
    const { copayCheck, prepayCheck, balanceCheck, partialBalanceCheck } =
      isCheckedObj;
    const partialBalanceAmount = partialBalanceCheck
      ? partialBalanceInfo.amount
      : null;
    if (copayCheck && prepayCheck && balanceCheck) {
      paymentType = partialBalanceCheck
        ? api.Payment.PaymentType.FeeAndPrepayAndPartialBalance
        : api.Payment.PaymentType.Total;
    } else if (copayCheck && prepayCheck) {
      paymentType = api.Payment.PaymentType.FeeAndPrepay;
    } else if (copayCheck && balanceCheck) {
      paymentType = partialBalanceCheck
        ? api.Payment.PaymentType.FeeAndPartialBalance
        : api.Payment.PaymentType.FeeAndBalance;
    } else if (prepayCheck && balanceCheck) {
      paymentType = partialBalanceCheck
        ? api.Payment.PaymentType.PrepayAndPartialBalance
        : api.Payment.PaymentType.PrepayAndBalance;
    } else if (copayCheck) {
      paymentType = api.Payment.PaymentType.Fee;
    } else if (prepayCheck) {
      paymentType = api.Payment.PaymentType.Prepay;
    } else if (balanceCheck) {
      paymentType = partialBalanceCheck
        ? api.Payment.PaymentType.PartialBalance
        : api.Payment.PaymentType.Balance;
    }

    const isUnchangedPaymentType =
      paymentInfo.selectedPaymentType === paymentType;
    const isUnchangedPartialBalanceAmount =
      paymentInfo.partialBalanceAmount === partialBalanceAmount;
    if (isUnchangedPaymentType && isUnchangedPartialBalanceAmount) {
      return handleContinue();
    }
    try {
      setLoading(true);
      const newData = await api.client.Payment.updatePaymentAmount({
        authenticationCode,
        previsitFormSetId: selectedFormSetId,
        paymentType,
        partialBalanceAmount,
      });
      setData(newData);
    } catch (err) {
      alert(t('We were unable to process your payment request.'));
      return;
    } finally {
      setLoading(false);
    }
    handleContinue();
  };

  const {
    currency,
    prepayAmount,
    previousBalance,
    visitFeeAmount: copayAmount,
  } = paymentInfo;
  const balanceAmount = isCheckedObj.partialBalanceCheck
    ? partialBalanceInfo.amount &&
      isPartialBalanceValid(partialBalanceInfo.amount, previousBalance)
      ? partialBalanceInfo.amount
      : 0
    : previousBalance;
  const formattedTotal = getFormattedCurrencyAmount(
    ((isCheckedObj.copayCheck && copayAmount) || 0) +
      ((isCheckedObj.balanceCheck && balanceAmount) || 0) +
      ((isCheckedObj.prepayCheck && prepayAmount) || 0),
    currency || 'usd',
  );

  const canSkipPayment =
    !isCheckedObj.copayCheck &&
    !isCheckedObj.prepayCheck &&
    !isCheckedObj.balanceCheck;
  const hasSingleListItem = displayTypes.length === 1;
  const headerText = hasSingleListItem
    ? `${t('Review & pay your')} ${t(displayTypes[0])}`
    : t(
        `You have a ${displayTypes
          .join(', ')
          .replace(
            /, ([^,]*)$/,
            ' and $1',
          )} due. Select the items you are able to pay.`,
      );

  const appointmentCostsData = [
    ...(showCopay
      ? [
          {
            checked: isCheckedObj.copayCheck,
            label: t(feeDescription),
            onChange: () => toggleCheck({ key: 'copayCheck' }),
            rowData: getFormattedCurrencyAmount(copayAmount, currency || 'usd'),
          },
        ]
      : []),
    ...(showPrepay
      ? [
          {
            checked: isCheckedObj.prepayCheck,
            label: t('Prepayment'),
            onChange: () => toggleCheck({ key: 'prepayCheck' }),
            rowData: getFormattedCurrencyAmount(
              prepayAmount ?? 0,
              currency || 'usd',
            ),
          },
        ]
      : []),
  ];

  return (
    <div>
      <div
        className={classNames(
          introPageStyles.Label,
          paymentsStyles.HeaderTextSpacing,
        )}
      >
        {headerText}
      </div>
      <VStack gap={16}>
        <Card size="large">
          <Card.Body>
            <VStack gap={12}>
              {(showCopay || showPrepay) &&
                appointmentCostsData.map(
                  ({ checked, label, onChange, rowData }) => {
                    return (
                      <React.Fragment key={label}>
                        <HStack
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Checkbox
                            checked={checked}
                            label={label}
                            onChange={onChange}
                          />
                          <Typography variant="title-large">
                            {rowData}
                          </Typography>
                        </HStack>
                        <hr className={styles.Divider} />
                      </React.Fragment>
                    );
                  },
                )}
              {showPreviousBalance && (
                <PriorBalance
                  isCheckedObj={isCheckedObj}
                  onClickCheck={toggleCheck}
                  previousBalance={previousBalance}
                  currency={currency}
                  partialBalanceInfo={partialBalanceInfo}
                  setPartialBalanceInfo={setPartialBalanceInfo}
                  showBalanceOnly={!showCopay && !showPrepay}
                />
              )}
              <HStack justifyContent="space-between" alignItems="center">
                <Typography variant="title-large" bold>
                  {t('Total')}
                </Typography>
                <Typography variant="title-large" bold>
                  {formattedTotal}
                </Typography>
              </HStack>
            </VStack>
          </Card.Body>
        </Card>
      </VStack>
      <VStack paddingTop={12}>
        <Alert
          children={
            infoMessage ||
            t(
              'Full payment is not required at this time but will save you time when you arrive.',
            )
          }
        />
      </VStack>
      <VStack paddingTop={32} gap={16}>
        <Button
          disabled={loading || canSkipPayment}
          onClick={() => updatePaymentAmountAndContinue()}
          children={t('Continue to payment')}
          fullWidth
        />
        <Button
          onClick={() => navigate(Page.SkipFeedback)}
          disabled={loading}
          children={t('Pay later')}
          variant="outlined"
          fullWidth
        />
      </VStack>
    </div>
  );
};

function mapStateToProps(state: store.All): StateProps {
  const { selectedFormSetId, formSets } = state;
  return {
    copayPrepayDisplaySetting:
      (selectedFormSetId !== null
        ? formSets[selectedFormSetId]?.practicePreference
            .copayPrepayDisplaySetting
        : null) ?? state.masterPracticePreference.copayPrepayDisplaySetting,
    previousBalanceCollectionSetting:
      (selectedFormSetId !== null
        ? formSets[selectedFormSetId]?.practicePreference
            .previousBalanceCollectionSetting
        : null) ??
      state.masterPracticePreference.previousBalanceCollectionSetting,
  };
}

export default connect(mapStateToProps)(BalancePage);
